import { EuiModal } from '@elastic/eui';
import { Global } from '@emotion/react';
import styled from '@emotion/styled';
import React, { FC } from 'react';

import { Button, ButtonVariant } from '@tecton/ComponentRedesign';
import { FlexGroup, FlexItem } from '../../_eui_components';

interface TectonModalProps {
  title?: string;
  body: React.ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
  variant?: 'positive' | 'negative' | 'neutral' | 'search' | 'global-search';
  cancelLabel?: string;
  confirmLabel?: string;
  confirmVariant?: ButtonVariant;
  isConfirmButtonLoading?: boolean;
  maxWidth?: boolean | number | string;
  ref?: React.RefObject<HTMLDivElement> | null;
}

// TODO: Remove this variant once global search is rolled out.
const SearchModalContainer = styled(EuiModal)(({ theme }) => ({
  display: 'flex',
  width: '600px',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.padding.m,
  flexShrink: 0,
  borderRadius: theme.border.radius.small,
  background: theme.colors.emptyShade,
  boxShadow: theme.shadow.xl,
  paddingBottom: theme.padding.l,
  marginTop: '20vh',

  '.euiModal__closeIcon': {
    insetInlineEnd: '12px',
    insetBlockStart: 'calc(50% - 12px)',
  },

  [`@media (max-width: ${theme.breakpoint.m}px)`]: {
    position: 'relative',
  },
}));

const GlobalSearchModalContainer = styled(EuiModal)(({ theme }) => ({
  display: 'flex',
  minWidth: '786px',
  maxWidth: '860px',
  width: '85vw',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.padding.m,
  flexShrink: 0,
  borderRadius: theme.border.radius.small,
  background: theme.colors.emptyShade,
  boxShadow: theme.shadow.xl,
  paddingBottom: theme.padding.l,
  marginTop: '20vh',
  inlineSize: '85vw',
  maxInlineSize: '860px',
  height: '52px',
  '.euiModal__closeIcon': {
    insetInlineEnd: '12px',
    insetBlockStart: 'calc(50% - 12px)',
  },

  [`@media (max-width: ${theme.breakpoint.m}px)`]: {
    position: 'relative',
    // Yes we need this. The rounded corners go away when we hit the breakpoint.
    borderRadius: theme.border.radius.small,
    background: theme.colors.emptyShade,
    boxShadow: theme.shadow.xl,
    paddingBottom: theme.padding.l,
  },
}));

const SearchModalContent = styled.div`
  display: flex;
  padding-top: ${({ theme }) => theme.padding.s};
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.padding.xl};
  align-self: stretch;
  color: ${({ theme }) => theme.colors.mediumShade};
  font-size: ${({ theme }) => theme.font.fontSizes.s};
  line-height: ${({ theme }) => theme.font.lineHeights.s};
`;

const SearchModal = React.forwardRef<HTMLDivElement, TectonModalProps>(({ body, onCancel }, ref) => {
  return (
    <SearchModalContainer onClose={onCancel}>
      <SearchModalContent ref={ref}>{body}</SearchModalContent>
    </SearchModalContainer>
  );
});

const GlobalSearchModal = React.forwardRef<HTMLDivElement, TectonModalProps>(({ body, onCancel }, ref) => {
  return (
    <GlobalSearchModalContainer onClose={onCancel}>
      <SearchModalContent ref={ref}>{body}</SearchModalContent>
    </GlobalSearchModalContainer>
  );
});

const ModalContainer = styled.div`
  display: flex;

  padding: ${({ theme }) => theme.padding.xl};
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.padding.l};
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.border.radius.small};
  background: ${({ theme }) => theme.colors.emptyShade};
  box-shadow: ${({ theme }) => theme.shadow.xl};
`;

const ModalTitle = styled.div`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.font.weight.medium};
`;

const ModalContent = styled.div`
  display: flex;
  padding-top: ${({ theme }) => theme.padding.s};
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.padding.xl};
  align-self: stretch;
  color: ${({ theme }) => theme.colors.mediumShade};
  font-size: ${({ theme }) => theme.font.fontSizes.s};
  line-height: ${({ theme }) => theme.font.lineHeights.s};
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${({ theme }) => theme.padding.l};
  align-self: stretch;
`;

const TectonModal: FC<TectonModalProps> = ({
  title,
  body,
  onCancel,
  onConfirm,
  variant = 'neutral',
  cancelLabel,
  confirmLabel,
  confirmVariant,
  isConfirmButtonLoading,
  maxWidth,
  ref = null,
}) => {
  if (variant === 'search') {
    return (
      <>
        <Global
          styles={{
            '.euiOverlayMask': {
              alignItems: 'flex-start',
            },
          }}
        />
        <SearchModal ref={ref} title={title} body={body} onCancel={onCancel} onConfirm={onConfirm} />
      </>
    );
  }

  if (variant === 'global-search') {
    return (
      <>
        <Global
          styles={{
            '.euiOverlayMask': {
              alignItems: 'flex-start',
            },
          }}
        />
        <GlobalSearchModal ref={ref} title={title} body={body} onCancel={onCancel} onConfirm={onConfirm} />
      </>
    );
  }

  let ActionButtons = <></>;
  switch (variant) {
    case 'neutral':
      ActionButtons = (
        <>
          <FlexItem grow={false}>
            <Button
              label={confirmLabel ?? 'Confirm'}
              onClick={onConfirm}
              variant={confirmVariant ?? 'primaryAction'}
              isLoading={isConfirmButtonLoading}
              testId="modal-confirm-button"
            />
          </FlexItem>
        </>
      );
      break;
    case 'negative':
      ActionButtons = (
        <>
          <FlexItem grow={false}>
            <Button
              label={confirmLabel ?? 'Confirm'}
              onClick={onConfirm}
              variant={confirmVariant ?? 'destructiveAction'}
              isLoading={isConfirmButtonLoading}
              testId="modal-confirm-button"
            />
          </FlexItem>
          <FlexItem grow={false}>
            <Button label={cancelLabel ?? 'Cancel'} onClick={onCancel} variant={'emptyAction'} />
          </FlexItem>
        </>
      );
      break;
    case 'positive':
      ActionButtons = (
        <>
          <FlexItem grow={false}>
            <Button
              label={confirmLabel ?? 'Confirm'}
              onClick={onConfirm}
              variant={confirmVariant ?? 'primaryAction'}
              isLoading={isConfirmButtonLoading}
              testId="modal-confirm-button"
            />
          </FlexItem>
          <FlexItem grow={false}>
            <Button label={cancelLabel ?? 'Cancel'} onClick={onCancel} variant={'emptyAction'} />
          </FlexItem>
        </>
      );
      break;
  }

  return (
    <EuiModal onClose={onCancel} data-testid="modal" maxWidth={maxWidth}>
      <ModalContainer ref={ref}>
        <div>
          <ModalTitle>{title}</ModalTitle>
          <ModalContent>{body}</ModalContent>
        </div>
        <ModalFooter>
          <FlexGroup direction="rowReverse" alignItems="flexEnd" gutterSize="m" wrap>
            {ActionButtons}
          </FlexGroup>
        </ModalFooter>
      </ModalContainer>
    </EuiModal>
  );
};

export default TectonModal;
